import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Users2, UserCog, FileText, Zap, PieChart as PieChartIcon } from 'lucide-react';
import { adminAPI } from '../services/adminApi';
import { useAuth } from '../context/AdminAuthContext';
import './AdminDashboard.css';

const RANGE_OPTIONS = [
  { value: 'today',   label: 'Today' },
  { value: '7d',       label: 'Last 7 Days' },
  { value: 'monthly',  label: 'Monthly' },
];

const TICK_INTERVAL = { today: 3, '7d': 0 };
const MONTHLY_TICK_INDEXES = [0, 5, 10]; 
const RANGE_LABEL = { today: 'today', '7d': 'this week', monthly: 'this year' };
const GRID_STROKE = 'rgba(148, 163, 184, 0.25)';
const STATUS_COLORS = { Pending: '#f59e0b', Completed: '#10b981' };
const INPUT_COLOR  = '#0d9488'; 
const OUTPUT_COLOR = '#6366f1'; 

const fmtCost = (n) => (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });

const Dashboard = () => {
  const { isSuperAdmin, user } = useAuth();
  const [overview, setOverview]               = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError]     = useState('');
  const [range, setRange]           = useState('today');
  const [chart, setChart]           = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState('');

  useEffect(() => {
    setOverviewLoading(true);
    adminAPI.getStatsOverview()
      .then(r => setOverview(r.data))
      .catch(err => setOverviewError(err.response?.data?.error || err.message || 'Unknown error'))
      .finally(() => setOverviewLoading(false));
  }, []);

  useEffect(() => {
    setChartLoading(true);
    setChartError('');
    adminAPI.getUsageStats(range)
      .then(r => setChart(r.data))
      .catch(err => setChartError(err.response?.data?.error || err.message || 'Unknown error'))
      .finally(() => setChartLoading(false));
  }, [range]);

  const fmtLabel = (label) => {
    const d = new Date(label);
    if (range === 'today')   return d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true, timeZone: 'Asia/Kolkata' }).replace(' ', '');
    if (range === 'monthly') return d.toLocaleDateString('en-US', { month: 'short', timeZone: 'Asia/Kolkata' });
    return d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Asia/Kolkata' }); // 7d
  };

  const usageData = chart?.usage || [];
  const monthlyTicks = range === 'monthly'
    ? MONTHLY_TICK_INDEXES.filter(i => usageData[i]).map(i => usageData[i].label)
    : undefined;

  const pieData = [
    { name: 'Pending',   value: overview?.pendingReports   ?? 0 },
    { name: 'Completed', value: overview?.completedReports ?? 0 },
  ];
  const hasReports = (overview?.pendingReports ?? 0) + (overview?.completedReports ?? 0) > 0;

  return (
    <div className="admin-dashboard">
      <h1 className="admin-page-title">Welcome, {user?.first_name} {user?.last_name}</h1>

      {overviewError && (
        <div className="admin-dashboard-error">
          Couldn't load dashboard data: <strong>{overviewError}</strong>
        </div>
      )}

      <div className="admin-stat-cards">
        <StatCard label="Total Reports" value={overview?.totalReports} Icon={FileText} tone="teal"   loading={overviewLoading} />
        <StatCard label="Users"         value={overview?.totalUsers}   Icon={Users2}   tone="indigo" loading={overviewLoading} />
        {isSuperAdmin && (
          <StatCard label="Admins" value={overview?.totalAdmins} Icon={UserCog} tone="amber" loading={overviewLoading} />
        )}
      </div>

      <div className="admin-dashboard-row">
        <div className="admin-chart-card admin-pie-card">
          <div className="admin-chart-header">
            <div className="admin-chart-title">
              <PieChartIcon size={16} className="admin-chart-title-icon" />
              <h2>Report Status</h2>
            </div>
          </div>

          {overviewLoading ? (
            <div className="admin-chart-loading">Loading…</div>
          ) : !hasReports ? (
            <div className="admin-chart-loading">No reports yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={70} outerRadius={108} paddingAngle={2}>
                  {pieData.map(entry => <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />)}
                </Pie>
                <Tooltip formatter={(value, name) => [value.toLocaleString(), name]} />
                <Legend
                  align="left"
                  verticalAlign="bottom"
                  iconType="circle"
                  wrapperStyle={{ fontSize: 12, paddingTop: 16 }}
                  formatter={(value) => {
                    const entry = pieData.find(d => d.name === value);
                    return `${value} (${entry?.value ?? 0})`;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="admin-chart-card admin-token-card">
          <div className="admin-chart-header">
            <div className="admin-chart-title">
              <Zap size={16} className="admin-chart-title-icon" />
              <h2>Token Consumption</h2>
            </div>
            <select className="admin-range-select" value={range} onChange={e => setRange(e.target.value)}>
              {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {!chartLoading && chart && (
            <div className="admin-chart-badges">
              <span className="admin-chart-badge tone-teal">
                {chart.rangeTotalTokens.toLocaleString()} total tokens {RANGE_LABEL[range]}
              </span>
              <span className="admin-chart-badge tone-teal-outline">
                {chart.rangeInputTokens.toLocaleString()} input
              </span>
              <span className="admin-chart-badge tone-indigo-outline">
                {chart.rangeOutputTokens.toLocaleString()} output
              </span>
              <span className="admin-chart-badge tone-amber-outline">
                {fmtCost(chart.rangeTotalCost)} cost
              </span>
            </div>
          )}

          {chartError && (
            <div className="admin-dashboard-error">
              Couldn't load usage data: <strong>{chartError}</strong>
            </div>
          )}

          {chartLoading ? (
            <div className="admin-chart-loading">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={usageData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="inputTokenFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={INPUT_COLOR} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={INPUT_COLOR} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="outputTokenFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={OUTPUT_COLOR} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={OUTPUT_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="0" stroke={GRID_STROKE} vertical={false} />
                <XAxis
                  dataKey="label"
                  tickFormatter={fmtLabel}
                  interval={range === 'monthly' ? undefined : TICK_INTERVAL[range]}
                  ticks={monthlyTicks}
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                <Tooltip
                  labelFormatter={fmtLabel}
                  formatter={(value, name) => {
                    if (name === 'prompt_tokens')     return [Number(value).toLocaleString(), 'Input Tokens'];
                    if (name === 'completion_tokens') return [Number(value).toLocaleString(), 'Output Tokens'];
                    return [Number(value).toLocaleString(), name];
                  }}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  height={28}
                  iconType="circle"
                  wrapperStyle={{ fontSize: 12 }}
                  formatter={(value) => (value === 'prompt_tokens' ? 'Input Tokens' : 'Output Tokens')}
                />
                <Area type="monotone" dataKey="prompt_tokens"     stackId="tokens" name="prompt_tokens"     stroke={INPUT_COLOR}  strokeWidth={2} fill="url(#inputTokenFill)" />
                <Area type="monotone" dataKey="completion_tokens" stackId="tokens" name="completion_tokens" stroke={OUTPUT_COLOR} strokeWidth={2} fill="url(#outputTokenFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, Icon, tone, loading }) => (
  <div className="admin-stat-card">
    <div className={`admin-stat-icon tone-${tone}`}><Icon size={20} /></div>
    <div>
      <div className="admin-stat-value">{loading ? '—' : (value ?? 0).toLocaleString()}</div>
      <div className="admin-stat-label">{label}</div>
    </div>
  </div>
);

export default Dashboard;